import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import type {
  CatalogServiceCategoriesListResponseData,
  CatalogServiceShowResponseData,
  ClientServiceCategoriesListResponseData,
} from '@/types/api/queries';

import { HttpClient } from '@/network/HttpClient';

// The tree renders the whole catalog at once — override the RTable default (100 rows).
const CATALOG_TREE_PAGE_SIZE = 1000;

export const catalogServiceKeys = {
  all: () => ['catalogServices'] as const,
  categories: (props: { url: string; filters?: Record<string, unknown> }) =>
    [
      ...catalogServiceKeys.all(),
      'categories',
      props.url,
      props.filters,
    ] as const,
  // Scope prefix — a sale / redeem / card use invalidates every client-priced
  // tree without knowing which client and location it was built for.
  clientCategoriesScope: () =>
    [...catalogServiceKeys.all(), 'clientCategories'] as const,
  clientCategories: (props: {
    url: string;
    params: { client_id: number; location_id: number };
  }) =>
    [
      ...catalogServiceKeys.clientCategoriesScope(),
      props.url,
      props.params,
    ] as const,
  detail: (props: { url: string }) =>
    [...catalogServiceKeys.all(), 'detail', props.url] as const,
};

export const catalogServiceQueries = {
  categories: (props: { url: string; filters?: Record<string, unknown> }) =>
    queryOptions({
      queryKey: catalogServiceKeys.categories(props),
      queryFn: () =>
        HttpClient.request<CatalogServiceCategoriesListResponseData>({
          method: 'GET',
          url: props.url,
          params: {
            size_per_page: CATALOG_TREE_PAGE_SIZE,
            ...(props.filters ? { filters: props.filters } : {}),
          },
        }),
      enabled: !!props.url,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }),

  // The client-priced service tree with the best card discount per service —
  // `client_id` / `location_id` are top-level query params, not RTable filters.
  clientCategories: (props: {
    url: string;
    params: { client_id: number; location_id: number };
  }) =>
    queryOptions({
      queryKey: catalogServiceKeys.clientCategories(props),
      queryFn: () =>
        HttpClient.request<ClientServiceCategoriesListResponseData>({
          method: 'GET',
          url: props.url,
          params: { size_per_page: CATALOG_TREE_PAGE_SIZE, ...props.params },
        }),
      enabled: !!props.url,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }),

  detail: (props: { url: string }) =>
    queryOptions({
      queryKey: catalogServiceKeys.detail(props),
      queryFn: () =>
        HttpClient.request<CatalogServiceShowResponseData>({
          method: 'GET',
          url: props.url,
        }),
      enabled: !!props.url,
      retry: false,
      refetchOnWindowFocus: true,
      placeholderData: keepPreviousData,
    }),
};
